import type {
  StripeSubscriptionPlan,
  SubscriptionAmbassadorPlan,
  SubscriptionBasicPlan,
  SubscriptionBookkeepingPlan,
  SubscriptionEInvoicingPlan,
  SubscriptionFreePlan,
  SubscriptionGrowMaxPlan,
  SubscriptionManualPlatform,
  SubscriptionMaxPlan,
  SubscriptionMobilePlatform,
  SubscriptionNoWebAccessPlan,
  SubscriptionPaidFranchiseePlan,
  SubscriptionPaidPlan,
  SubscriptionPlatform,
  SubscriptionProMaxPlan,
  SubscriptionPromoPlatform,
  SubscriptionProPlan,
  SubscriptionSmallPlan,
  SubscriptionTandemPlan,
  SubscriptionTaxesPlan,
  SubscriptionTrialPlan,
  SubscriptionWebPlatform,
} from 'components/pages/Subscriptions/Subscription.enum';
import {
  stripeSubscriptionPlans,
  subscriptionAmbassadorPlans,
  subscriptionBasicPlans,
  subscriptionBookkeepingPlans,
  subscriptionEInvoicingPlans,
  subscriptionFreePlans,
  subscriptionGrowMaxPlans,
  subscriptionManualPlatforms,
  subscriptionMaxPlans,
  subscriptionMobilePlatforms,
  subscriptionNoWebAccessPlans,
  subscriptionPaidFranchiseePlans,
  subscriptionPaidPlans,
  subscriptionPlatforms,
  subscriptionProMaxPlans,
  subscriptionPromoPlatforms,
  subscriptionProPlans,
  subscriptionSmallPlans,
  subscriptionTandemPlans,
  subscriptionTaxesPlans,
  subscriptionTrialPlans,
  subscriptionWebPlatforms,
} from 'components/pages/Subscriptions/Subscription.enum';
import dayjs from 'dayjs';
import type { ISubscriptionPlan, IUser } from 'types/users.types';
import { SubscriptionPlanNameEnum } from 'types/users.types';

export const isSubscriptionFreePlan = (planName: string | undefined): boolean =>
  subscriptionFreePlans.includes(planName as SubscriptionFreePlan);

export const isSubscriptionTrialPlan = (
  planName: string | undefined,
): boolean =>
  subscriptionTrialPlans.includes(planName as SubscriptionTrialPlan);

export const isSubscriptionBasicPlan = (
  planName: string | undefined,
): boolean =>
  subscriptionBasicPlans.includes(planName as SubscriptionBasicPlan);

export const isSubscriptionProPlan = (planName: string | undefined): boolean =>
  subscriptionProPlans.includes(planName as SubscriptionProPlan);

export const isSubscriptionProMaxPlan = (
  planName: string | undefined,
): boolean =>
  subscriptionProMaxPlans.includes(planName as SubscriptionProMaxPlan);

export const isSubscriptionProMax12Plan = (
  planName: string | undefined,
): boolean => planName === SubscriptionPlanNameEnum.pro_max12;

export const isSubscriptionSmallPlan = (
  planName: string | undefined,
): boolean =>
  subscriptionSmallPlans.includes(planName as SubscriptionSmallPlan);

export const isSubscriptionGrowMaxPlan = (
  planName: string | undefined,
): boolean =>
  subscriptionGrowMaxPlans.includes(planName as SubscriptionGrowMaxPlan);

export const isSubscriptionMaxPlan = (planName: string | undefined): boolean =>
  subscriptionMaxPlans.includes(planName as SubscriptionMaxPlan);

export const isSubscriptionTaxesPlan = (
  planName: string | undefined,
): boolean =>
  subscriptionTaxesPlans.includes(planName as SubscriptionTaxesPlan);

export const isSubscriptionBookkeepingPlan = (
  planName: string | undefined,
): boolean =>
  subscriptionBookkeepingPlans.includes(
    planName as SubscriptionBookkeepingPlan,
  );

export const isSubscriptionEInvoicingPlan = (
  planName: string | undefined,
): boolean =>
  subscriptionEInvoicingPlans.includes(planName as SubscriptionEInvoicingPlan);

export const isSubscriptionPaidPlan = (planName: string | undefined): boolean =>
  subscriptionPaidPlans.includes(planName as SubscriptionPaidPlan);

export const isSubscriptionPaidFranchiseePlan = (
  planName: string | undefined,
): boolean =>
  subscriptionPaidFranchiseePlans.includes(
    planName as SubscriptionPaidFranchiseePlan,
  );

export const isSubscriptionAmbassadorPlan = (
  planName: string | undefined,
): boolean =>
  subscriptionAmbassadorPlans.includes(planName as SubscriptionAmbassadorPlan);

export const isSubscriptionTandemPlan = (
  planName: string | undefined,
): boolean =>
  subscriptionTandemPlans.includes(planName as SubscriptionTandemPlan);

export const isSubscriptionNoWebAccessPlan = (
  planName: string | undefined,
): boolean =>
  subscriptionNoWebAccessPlans.includes(
    planName as SubscriptionNoWebAccessPlan,
  );

export const isSubscriptionPlan = (planName: string | undefined): boolean =>
  stripeSubscriptionPlans.includes(planName as StripeSubscriptionPlan);

export const isSubscriptionManualPlatform = (
  platform: string | undefined,
): boolean =>
  subscriptionManualPlatforms.includes(platform as SubscriptionManualPlatform);

export const isSubscriptionMobilePlatform = (
  platform: string | undefined,
): boolean =>
  subscriptionMobilePlatforms.includes(platform as SubscriptionMobilePlatform);

export const isSubscriptionWebPlatform = (
  platform: string | undefined,
): boolean =>
  subscriptionWebPlatforms.includes(platform as SubscriptionWebPlatform);

export const isSubscriptionPromoPlatform = (
  platform: string | undefined,
): boolean =>
  subscriptionPromoPlatforms.includes(platform as SubscriptionPromoPlatform);

export const isSubscriptionPlatform = (platform: string | undefined): boolean =>
  subscriptionPlatforms.includes(platform as SubscriptionPlatform);

export const isMonthlySubscription = (plan: ISubscriptionPlan): boolean => {
  return (
    [
      SubscriptionPlanNameEnum.pro1,
      SubscriptionPlanNameEnum.small1,
      SubscriptionPlanNameEnum.small_max1,
      SubscriptionPlanNameEnum.pro_max1,
    ].includes(plan?.planInternalName) &&
    plan?.fromPlatform === 'stripe' &&
    dayjs().subtract(2, 'month').isAfter(dayjs(plan.start))
  );
};

export const isEligibleForOptInBeta = (plan: ISubscriptionPlan) => {
  return (
    subscriptionProPlans.includes(plan?.planInternalName as any) &&
    dayjs().subtract(15, 'month').isAfter(dayjs(plan.start))
  );
};

export const isFreeOrTrialWithoutPreviousSubscription = (
  user: IUser,
): boolean => {
  if (!user.subscriptionPlan) return false;

  const currentPlan = user.subscriptionPlan.planInternalName;

  if (
    !isSubscriptionFreePlan(currentPlan) &&
    !isSubscriptionTrialPlan(currentPlan)
  ) {
    return false;
  }

  return (
    !user.subscriptionPlan.firstSubscriptionDate &&
    !user.subscriptionPlan.lastSubscriptionDate
  );
};
